// src/pages/PromoCodesPage.js
// Admin UI to manage per-event promo codes. Supports percentage or flat discounts.
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';

export default function PromoCodesPage() {
  const { isManagerOrAbove } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState(() => {
    const fromUrl = searchParams.get('event');
    return fromUrl ? parseInt(fromUrl, 10) : null;
  });
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // null | 'new' | { ...code }

  // Load events for the dropdown
  useEffect(() => {
    api.get('/promo-codes/events-list')
      .then(res => setEvents(Array.isArray(res.data) ? res.data : []))
      .catch(() => toast.error('Could not load events.'));
  }, []);

  // Load codes when event changes
  useEffect(() => {
    if (!selectedEventId) { setCodes([]); return; }
    setSearchParams({ event: String(selectedEventId) });
    setLoading(true);
    api.get(`/promo-codes?eventId=${selectedEventId}`)
      .then(res => setCodes(res.data || []))
      .catch(() => toast.error('Could not load codes.'))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEventId]);

  const refresh = () => {
    if (!selectedEventId) return;
    api.get(`/promo-codes?eventId=${selectedEventId}`).then(res => setCodes(res.data || []));
  };

  const handleSave = async (formData) => {
    try {
      if (editing === 'new') {
        await api.post('/promo-codes', { ...formData, eventId: selectedEventId });
        toast.success('Code created.');
      } else {
        await api.patch(`/promo-codes/${editing.id}`, formData);
        toast.success('Code updated.');
      }
      setEditing(null);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Save failed.');
      throw err;
    }
  };

  const handleToggleActive = async (code) => {
    try {
      await api.patch(`/promo-codes/${code.id}`, { isActive: !code.is_active });
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not update.');
    }
  };

  const copyPromoLink = (code) => {
    const url = `${window.location.origin}/embed/events/${selectedEventId}?promo=${code.code}`;
    navigator.clipboard.writeText(url).then(
      () => toast.success('Link copied to clipboard'),
      () => toast.error('Could not copy. URL: ' + url)
    );
  };

  const handleDelete = async (code) => {
    if (!window.confirm(`Delete the code "${code.code}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/promo-codes/${code.id}`);
      toast.success('Code deleted.');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not delete.');
    }
  };

  const upcomingEvents = events.filter(e =>
    !e.event_date || new Date(e.event_date) >= new Date(new Date().toDateString())
  );
  const pastEvents = events.filter(e =>
    e.event_date && new Date(e.event_date) < new Date(new Date().toDateString())
  );

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Promo Codes</h1>
          <p className="page-subtitle">Discount codes for event registrations.</p>
        </div>
        {selectedEventId && isManagerOrAbove() && (
          <button className="btn btn-primary" onClick={() => setEditing('new')}>
            + Add Promo Code
          </button>
        )}
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-body">
          <label style={{ display: 'block', fontWeight: 600, marginBottom: 6 }}>Event</label>
          <select
            value={selectedEventId || ''}
            onChange={e => setSelectedEventId(e.target.value ? parseInt(e.target.value, 10) : null)}
            style={{
              width: '100%', maxWidth: 500, padding: '10px 12px',
              fontSize: '0.95rem', border: '1px solid var(--gray-300)',
              borderRadius: 6, background: '#fff',
            }}
          >
            <option value="">— Select an event —</option>
            {upcomingEvents.length > 0 && (
              <optgroup label="Upcoming">
                {upcomingEvents.map(ev => (
                  <option key={ev.id} value={ev.id}>
                    {ev.name} {ev.event_date ? `— ${formatDate(ev.event_date)}` : ''}
                  </option>
                ))}
              </optgroup>
            )}
            {pastEvents.length > 0 && (
              <optgroup label="Past">
                {pastEvents.slice(0, 50).map(ev => (
                  <option key={ev.id} value={ev.id}>
                    {ev.name} — {formatDate(ev.event_date)}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
        </div>
      </div>

      {!selectedEventId ? (
        <div className="card">
          <div className="card-body" style={{ textAlign: 'center', padding: 48, color: 'var(--gray-500)' }}>
            Select an event above to manage its promo codes.
          </div>
        </div>
      ) : loading ? (
        <div className="card"><div className="card-body" style={{ textAlign: 'center', padding: 32 }}>Loading…</div></div>
      ) : codes.length === 0 ? (
        <div className="card">
          <div className="card-body" style={{ textAlign: 'center', padding: 48, color: 'var(--gray-500)' }}>
            <p style={{ marginBottom: 12 }}>No promo codes for this event yet.</p>
            {isManagerOrAbove() && (
              <button className="btn btn-primary" onClick={() => setEditing('new')}>
                + Create First Code
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="card">
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                <th style={thStyle}>Code</th>
                <th style={thStyle}>Discount</th>
                <th style={thStyle}>Usage</th>
                <th style={thStyle}>Expires</th>
                <th style={thStyle}>Status</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {codes.map(code => (
                <tr key={code.id} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                  <td style={tdStyle}>
                    <code style={{ background: 'var(--gray-100)', padding: '2px 8px', borderRadius: 3, fontWeight: 600, fontSize: '0.9rem' }}>
                      {code.code}
                    </code>
                  </td>
                  <td style={tdStyle}>{formatDiscount(code)}</td>
                  <td style={tdStyle}>
                    {code.times_used}{code.max_uses != null ? ` / ${code.max_uses}` : ' (unlimited)'}
                  </td>
                  <td style={tdStyle}>
                    {code.expires_at ? formatDate(code.expires_at) : <span style={{ color: 'var(--gray-400)' }}>Never</span>}
                  </td>
                  <td style={tdStyle}>
                    <span style={{
                      fontSize: '0.75rem', fontWeight: 700, padding: '2px 8px', borderRadius: 10,
                      background: code.is_active ? '#dcfce7' : 'var(--gray-200)',
                      color:      code.is_active ? '#15803d' : 'var(--gray-600)',
                      textTransform: 'uppercase',
                    }}>
                      {code.is_active ? 'Active' : 'Disabled'}
                    </span>
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>
                    {isManagerOrAbove() && (
                      <>
                        <button className="btn btn-sm btn-secondary" onClick={() => copyPromoLink(code)} style={{ marginRight: 4 }} title="Copy shareable link with this promo pre-applied">
                          🔗 Copy Link
                        </button>
                        <button className="btn btn-sm btn-secondary" onClick={() => setEditing(code)} style={{ marginRight: 4 }}>
                          Edit
                        </button>
                        <button className="btn btn-sm btn-secondary" onClick={() => handleToggleActive(code)} style={{ marginRight: 4 }}>
                          {code.is_active ? 'Disable' : 'Enable'}
                        </button>
                        <button className="btn btn-sm" onClick={() => handleDelete(code)} style={{ color: '#b91c1c' }}>
                          Delete
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <PromoCodeModal
          initial={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSave={handleSave}
        />
      )}
    </div>
  );
}

// ─── PromoCodeModal ────────────────────────────────────────────────────────
function PromoCodeModal({ initial, onClose, onSave }) {
  const [form, setForm] = useState({
    code:          initial?.code || '',
    discountType:  initial?.discount_type || 'percentage',
    discountValue: initial ? String(initial.discount_value) : '',
    maxUses:       initial?.max_uses != null ? String(initial.max_uses) : '',
    expiresAt:     initial?.expires_at ? toLocalDatetime(initial.expires_at) : '',
    isActive:      initial ? initial.is_active : true,
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave({
        code:          form.code,
        discountType:  form.discountType,
        discountValue: parseFloat(form.discountValue),
        maxUses:       form.maxUses === '' ? null : parseInt(form.maxUses, 10),
        expiresAt:     form.expiresAt || null,
        isActive:      form.isActive,
      });
    } catch (e) { /* parent toasts */ }
    setSaving(false);
  };

  return (
    <div style={modalOverlay} onClick={onClose}>
      <div style={modalContent} onClick={e => e.stopPropagation()}>
        <div style={modalHeader}>
          <h3 style={{ margin: 0 }}>{initial ? 'Edit Promo Code' : 'New Promo Code'}</h3>
          <button onClick={onClose} style={closeBtn}>×</button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: 20 }}>
          <div style={fgStyle}>
            <label style={lblStyle}>Code *</label>
            <input
              type="text" required value={form.code}
              onChange={e => set('code', e.target.value)}
              placeholder="EarlyBird" style={inputStyle}
              autoFocus
            />
            <div style={hintStyle}>
              Letters, numbers, dashes, underscores. Any capitalization is fine — codes are matched case-insensitively.
            </div>
          </div>

          <div style={fgStyle}>
            <label style={lblStyle}>Discount Type *</label>
            <div style={{ display: 'flex', gap: 16 }}>
              <label style={radioStyle}>
                <input type="radio" name="dt" value="percentage"
                  checked={form.discountType === 'percentage'}
                  onChange={() => set('discountType', 'percentage')} />
                Percentage off (%)
              </label>
              <label style={radioStyle}>
                <input type="radio" name="dt" value="flat"
                  checked={form.discountType === 'flat'}
                  onChange={() => set('discountType', 'flat')} />
                Flat dollar amount ($)
              </label>
            </div>
          </div>

          <div style={fgStyle}>
            <label style={lblStyle}>
              Discount Value * {form.discountType === 'percentage' ? '(0-100)' : '(in dollars)'}
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {form.discountType === 'flat' && <span style={{ fontSize: 18, color: 'var(--gray-600)' }}>$</span>}
              <input
                type="number" required step="0.01" min="0.01"
                max={form.discountType === 'percentage' ? '100' : undefined}
                value={form.discountValue}
                onChange={e => set('discountValue', e.target.value)}
                placeholder={form.discountType === 'percentage' ? '10' : '25.00'}
                style={{ ...inputStyle, flex: 1 }}
              />
              {form.discountType === 'percentage' && <span style={{ fontSize: 18, color: 'var(--gray-600)' }}>%</span>}
            </div>
            <div style={hintStyle}>
              Example: {form.discountType === 'percentage'
                ? 'Enter 10 for 10% off, 50 for 50% off, 100 for free'
                : 'Enter 25 for $25 off, 50 for $50 off'}
            </div>
          </div>

          <div style={{ ...fgStyle, display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <label style={lblStyle}>Max Uses</label>
              <input
                type="number" min="1" value={form.maxUses}
                onChange={e => set('maxUses', e.target.value)}
                placeholder="Unlimited" style={inputStyle}
              />
              <div style={hintStyle}>Blank = unlimited</div>
            </div>
            <div style={{ flex: 1 }}>
              <label style={lblStyle}>Expires At</label>
              <input
                type="datetime-local" value={form.expiresAt}
                onChange={e => set('expiresAt', e.target.value)}
                style={inputStyle}
              />
              <div style={hintStyle}>Blank = never expires</div>
            </div>
          </div>

          <div style={fgStyle}>
            <label style={radioStyle}>
              <input type="checkbox" checked={form.isActive}
                onChange={e => set('isActive', e.target.checked)} />
              Active (customers can use this code)
            </label>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : (initial ? 'Save Changes' : 'Create Code')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Helpers ───────────────────────────────────────────────────────────────
function formatDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
function formatDiscount(c) {
  const v = parseFloat(c.discount_value);
  return c.discount_type === 'percentage' ? `${v}% off` : `$${v.toFixed(2)} off`;
}
function toLocalDatetime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// ─── Styles ────────────────────────────────────────────────────────────────
const thStyle = { textAlign: 'left', padding: '12px 14px', fontSize: '0.8rem', fontWeight: 600,
                   color: 'var(--gray-600)', textTransform: 'uppercase', letterSpacing: 0.3 };
const tdStyle = { padding: '12px 14px', fontSize: '0.9rem', color: 'var(--gray-800)', verticalAlign: 'middle' };
const fgStyle = { marginBottom: 16 };
const lblStyle = { display: 'block', fontWeight: 600, marginBottom: 4, fontSize: '0.85rem', color: 'var(--gray-700)' };
const inputStyle = {
  width: '100%', padding: '8px 12px', fontSize: '0.95rem',
  border: '1px solid var(--gray-300)', borderRadius: 6,
  background: '#fff', boxSizing: 'border-box',
};
const radioStyle = { display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: '0.9rem' };
const hintStyle = { fontSize: '0.75rem', color: 'var(--gray-500)', marginTop: 3 };
const modalOverlay = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center',
  justifyContent: 'center', zIndex: 1000, padding: 20,
};
const modalContent = {
  background: '#fff', borderRadius: 8, width: '100%', maxWidth: 540,
  maxHeight: '90vh', overflow: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
};
const modalHeader = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '16px 20px', borderBottom: '1px solid var(--gray-200)',
};
const closeBtn = {
  background: 'none', border: 'none', fontSize: 28, cursor: 'pointer',
  color: 'var(--gray-500)', padding: 0, lineHeight: 1, width: 32,
};
